import { Add, EditOutlined, PlaceOutlined } from '@mui/icons-material';
import { MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppUI } from 'src/context/AppUIProvider/useAppUI';
import useAuthContext from 'src/context/AuthProvider/useAuthContext';
import AddressLabel from 'src/features/addresses/presentation/components/AddressLabel';
import useAddressList from '../../hooks/useAddressList';
import useAddressMutations from '../../hooks/useAddressMutations';
import * as Styles from './AddressSelector.styles';
import { EventNamesMap, useAmplitude } from 'src/context/AmplitudeProvider';

const AddressSelector = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, session, isB2b } = useAuthContext();
  const navigate = useNavigate();
  const { alertNotification } = useAppUI();
  const { setMainAddress } = useAddressMutations();
  const { logEvent } = useAmplitude();
  const { addresses, mainAddress } = useAddressList();
  const [selectedPk, setSelectedPk] = useState(mainAddress?.pk);

  useEffect(() => {
    if (!selectedPk && mainAddress?.pk) {
      setSelectedPk(mainAddress.pk);
    }
    // TODO: simplify this block and find a more robust solution if possible
    if (selectedPk && mainAddress?.pk && selectedPk !== mainAddress.pk) {
      if (setMainAddress.isIdle) {
        // addresses out-of-sync because user just added a new one → update selector
        setSelectedPk(mainAddress.pk);
      }
      if (setMainAddress.isSuccess) {
        // use mutation's returned pk to avoid stale cache during invalidation
        setSelectedPk(setMainAddress.data.pk);
      }
    }
  }, [selectedPk, mainAddress, setMainAddress]);

  const handleSetMainAddress = async (pk: number) => {
    if (!session || !user?.pk) {
      navigate('/login');
      return;
    }
    setSelectedPk(pk);
    setMainAddress.mutate(
      { accessToken: session.accessToken, userId: user.pk, addressId: pk },
      {
        onError: () => {
          alertNotification.error('Error al cambiar la dirección principal');
          setSelectedPk(mainAddress?.pk);
        },
      }
    );
  };

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleAddressChange = (event: SelectChangeEvent<number | string>) => {
    const selectedPk = event.target.value;
    const selectedAddress = addresses.find(
      (address) => address.pk === Number(selectedPk)
    );
    if (selectedAddress) {
      handleSetMainAddress(selectedAddress.pk);
    }
  };

  return (
    <Select
      fullWidth
      variant="filled"
      value={selectedPk || ''}
      onChange={handleAddressChange}
      onClick={handleToggle}
      startAdornment={
        selectedPk ? (
          <PlaceOutlined sx={{ pr: 0.8 }} fontSize="medium" />
        ) : undefined
      }
      input={<Styles.SelectInput />}
      displayEmpty
    >
      <MenuItem value="" disabled>
        Selecciona una dirección
      </MenuItem>
      {addresses.map((address) => (
        <MenuItem
          key={address.pk}
          value={address.pk}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 0,
          }}
        >
          <AddressLabel address={address} />
        </MenuItem>
      ))}
      {!isB2b
        ? [
            <MenuItem
              key="new-address-item"
              onClick={() => {
                try {
                  logEvent(EventNamesMap.AddAddressClicked, {});
                } catch {
                  console.error('Error logging add address clicked');
                }
                navigate('/address/new');
              }}
              sx={{ gap: 1 }}
            >
              <Add fontSize="small" />
              Agregar dirección
            </MenuItem>,
            <MenuItem
              key="edit-address-item"
              onClick={() => navigate('/addresslist', { state: { from: '/' } })}
              sx={{ gap: 1 }}
            >
              <EditOutlined fontSize="small" />
              Editar direcciones
            </MenuItem>,
          ]
        : null}
    </Select>
  );
};

export default AddressSelector;
